import React, { useRef } from 'react';
import { GiftPresentation } from '../GiftPresentation';
import { GiftLine } from '../GiftLine';
import styles from './gift-bag.module.css';
import PersonalisationText from '../PersonalisationText';
import WaxStamp from '../WaxStamp';
import { useViewModel } from './useViewModal';

/**
 * @typedef {import('../../context/giftWrap-typedefs').GiftWrapOptions} GiftWrapOptions
 */

/**
 * Gift bag component for selecting and customizing gift wrap options
 * @param {Object} props - Component props
 * @param {GiftWrapOptions} props.product - The gift bag product data
 * @param {Array<GiftWrapOptions>} props.stamps - The stamp configuration data
 * @param {function} props.onScrollToElement - Function to scroll element into view
 */
export function GiftBagOrBox({ product, stamps, onScrollToElement }) {
  const optionsRef = useRef(null);
  const firstOptionRef = useRef(null);
  const {
    isExpanded,
    addPersonalisation,
    setAddPersonalisation,
    addWaxStamp,
    setAddWaxStamp,
    personalisationText,
    onChangePersonalisationText,
    selectedDesign,
    onChangeWasStamp,
    giftInfo,
    hasPersonalisationText,
    hasWaxStamp,
    handleToggleSelection,
    isSelected
  } = useViewModel({
    parentId: product.product_id,
    onScrollToElement,
    firstOptionRef,
    optionsRef
  });
  const { type } = product;
  let stamp = null;
  // Since stamps are now pre-filtered by type, we can use the first (and likely only) stamp
  if (stamps && stamps.length > 0) {
    stamp = stamps[0];
  }

  return (
    <>
      {/* changed: use full border and relative positioning; add top-right check icon centered on the corner when selected */}
      <div
        className={`${styles['giftBag']} ${isExpanded ? styles['giftBag--expanded'] : ''} ${isSelected ? styles['giftBag--selected'] : ''}`}
      >
        {isSelected && (
          <span className={styles['giftBag__checkmark']}>
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10 3L4.5 9L2 6.5"
                stroke="var(--color-white)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
        <div
          onClick={handleToggleSelection}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleSelection();
            }
          }}
          role="button"
          tabIndex={0}
          style={{ position: 'relative' }}
        >
          <GiftLine
            parentId={product.product_id}
            imageUrl={product.imageUrl}
            title={product.title}
            description={product.description}
            price={product.price}
            isExpanded={isExpanded}
          />
        </div>

        {isExpanded && (
          <div ref={optionsRef} className={styles['giftBag__options']}>
            {/* Personalisation Text (molecule) */}
            {hasPersonalisationText && (
              <div ref={firstOptionRef}>
                <PersonalisationText
                  price={5}
                  enabled={addPersonalisation}
                  onToggle={(v) => setAddPersonalisation(v)}
                  text={personalisationText}
                  onTextChange={onChangePersonalisationText}
                  maxLength={16}
                  itemType={type}
                />
              </div>
            )}

            {/* Wax Stamp (molecule) */}
            {hasWaxStamp && stamp && (
              <div ref={!hasPersonalisationText ? firstOptionRef : null}>
                <WaxStamp
                  price={1}
                  enabled={addWaxStamp}
                  onToggle={(v) => setAddWaxStamp(v)}
                  design={selectedDesign}
                  onDesignChange={onChangeWasStamp}
                  options={(stamp.variants || []).map((o) => ({ label: o.title, value: o.id }))}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {giftInfo && <GiftPresentation />}
    </>
  );
}
